import React from 'react';
import SEOHead from '../components/SEOHead';
import { motion } from 'framer-motion';

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen bg-white py-20">
      <SEOHead page="accessibility" />
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Accessibility Statement</h1>
            <p className="text-lg text-slate-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="prose prose-slate max-w-none space-y-8">
            <div>
              <p className="text-slate-600">
                Digian.ai is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards to achieve this.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Our Commitment</h2>
              <p className="text-slate-600">
                We aim to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 at Level AA. These guidelines explain how to make web content more accessible to people with disabilities, including visual, auditory, physical, speech, cognitive, language, learning, and neurological disabilities.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Measures We Take</h2>
              <p className="text-slate-600 mb-4">Digian.ai takes the following measures to ensure accessibility:</p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Include accessibility as part of our mission and design practices</li>
                <li>Use semantic HTML to support screen readers and assistive technologies</li>
                <li>Provide sufficient colour contrast ratios across all text and UI components</li>
                <li>Ensure all interactive elements are keyboard-navigable</li>
                <li>Provide descriptive alt text for meaningful images</li>
                <li>Use ARIA labels and landmarks where appropriate to improve screen reader navigation</li>
                <li>Test key user journeys with assistive technologies</li>
                <li>Avoid content that flashes more than three times per second</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">3. Conformance Status</h2>
              <p className="text-slate-600 mb-4">
                We are partially conformant with WCAG 2.1 Level AA. "Partially conformant" means that some parts of the content do not fully conform to the accessibility standard. We are actively working to address known gaps, including:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Improving keyboard focus visibility on all interactive elements</li>
                <li>Enhancing form error messages to be programmatically associated with their fields</li>
                <li>Reviewing third-party embedded content for accessibility compliance</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Technical Specifications</h2>
              <p className="text-slate-600 mb-4">
                Our website relies on the following technologies for conformance with WCAG 2.1:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>HTML5</li>
                <li>CSS3 (including Tailwind CSS)</li>
                <li>JavaScript (React 19)</li>
                <li>WAI-ARIA 1.1</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Supported Assistive Technologies</h2>
              <p className="text-slate-600 mb-4">
                We aim to support the following combinations of assistive technology and browser:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>NVDA + Firefox (Windows)</li>
                <li>JAWS + Chrome (Windows)</li>
                <li>VoiceOver + Safari (macOS / iOS)</li>
                <li>TalkBack + Chrome (Android)</li>
                <li>Keyboard-only navigation on all major browsers</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Known Limitations</h2>
              <p className="text-slate-600 mb-4">
                Despite our best efforts, some content may not yet meet all accessibility requirements. Known limitations include:
              </p>
              <ul className="list-disc list-inside text-slate-600 space-y-2">
                <li>Some third-party components may not be fully accessible; we are working with vendors to address these issues.</li>
                <li>PDF documents linked from the site may not be fully screen reader compatible; please contact us for an accessible alternative.</li>
                <li>Video content may not have captions; we are prioritising this work.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Feedback and Contact</h2>
              <p className="text-slate-600 mb-4">
                We welcome your feedback on the accessibility of our website. If you experience any barriers or have suggestions for improvement, please contact us:
              </p>
              <div className="bg-slate-50 p-6 rounded-lg space-y-2">
                <p className="text-slate-600"><strong>Email:</strong> <a href="mailto:contact@digian.ai" className="text-indigo-600 hover:text-indigo-800">contact@digian.ai</a></p>
                <p className="text-slate-600"><strong>Website:</strong> <a href="https://digian.ai" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800">digian.ai</a></p>
              </div>
              <p className="text-slate-600 mt-4">
                We aim to respond to accessibility feedback within 5 business days and to resolve issues within 30 days where feasible.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Formal Complaints</h2>
              <p className="text-slate-600">
                If you are not satisfied with our response to your accessibility concern, you may contact the relevant authority in your jurisdiction. For EU residents, this may be your national data protection or equality body. For UK residents, the Equality and Human Rights Commission (EHRC) enforces the Equality Act 2010.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AccessibilityPage;
